import type { Map as MapboxMap } from "mapbox-gl";

import type { FloorMapImage } from "./floor-map-images";

import { MAP_OVERLAY_FILL_LAYER_ID } from "../overlays/overlay-layers";

const sourceId = (floorId: string) => `sitescene-floor-image-${floorId}`;
const layerId = (floorId: string) => `sitescene-floor-image-layer-${floorId}`;

const removeFloorImage = (map: MapboxMap, floorId: string) => {
	const layer = layerId(floorId);
	const source = sourceId(floorId);
	if (map.getLayer(layer)) map.removeLayer(layer);
	if (map.getSource(source)) map.removeSource(source);
};

/**
 * Draw floor-plan images under zone overlays.
 * Coordinates are Mapbox image-source order: top-left, top-right,
 * bottom-right, bottom-left.
 *
 * `previousUrls` maps floor id → the URL last given to Mapbox, so a new
 * image replaces the source and a corner move only calls `setCoordinates`.
 */
export const syncFloorImageLayers = (
	map: MapboxMap,
	images: readonly FloorMapImage[],
	previousUrls: ReadonlyMap<string, string>,
) => {
	const nextUrls = new Map(images.map((image) => [image.id, image.url]));

	for (const floorId of previousUrls.keys()) {
		if (!nextUrls.has(floorId)) removeFloorImage(map, floorId);
	}

	const beforeId = map.getLayer(MAP_OVERLAY_FILL_LAYER_ID)
		? MAP_OVERLAY_FILL_LAYER_ID
		: undefined;

	for (const image of images) {
		const source = sourceId(image.id);
		const layer = layerId(image.id);
		const existing = map.getSource(source);
		const opacity = image.opacity ?? 1;
		const sameUrl = previousUrls.get(image.id) === image.url;

		if (existing && existing.type === "image" && sameUrl) {
			existing.setCoordinates(image.corners);
			if (map.getLayer(layer)) {
				map.setPaintProperty(layer, "raster-opacity", opacity);
			}
			continue;
		}

		if (map.getLayer(layer)) map.removeLayer(layer);
		if (map.getSource(source)) map.removeSource(source);

		map.addSource(source, {
			coordinates: image.corners,
			type: "image",
			url: image.url,
		});
		const rasterLayer = {
			id: layer,
			paint: {
				"raster-fade-duration": 0,
				"raster-opacity": opacity,
			},
			source,
			type: "raster" as const,
		};
		if (beforeId) {
			map.addLayer(rasterLayer, beforeId);
		} else {
			map.addLayer(rasterLayer);
		}
	}

	return nextUrls;
};

export const removeFloorImageLayers = (
	map: MapboxMap,
	floorIds: Iterable<string>,
) => {
	for (const floorId of floorIds) {
		removeFloorImage(map, floorId);
	}
};
